import React from "react";
import { useImmer } from "use-immer";

import {
  ColorError,
  ColorSuccess,
  IgButton,
  IgSelect,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  CartTransformFeaturesType,
  ConfigurationDto,
  OrganizationDto,
} from "@intelligems/ig-types";
import { Divider, Grid, MenuItem, SelectChangeEvent } from "@mui/material";

import { usePatchConfigurations } from "../../../api/configurations/patch-configurations";
import { useConfiguration } from "../../../api/configurations/use-configuration";
import {
  useGetOrg,
  useGetOrgId,
} from "../../../api/organizations/get-organization";
import { useSyncPricingFunction } from "../../../api/price-functions/sync-pricing-functions";
import { useGetCartTransforms } from "../../../api/shops/get-cart-transforms";
import { SkeletonGroup } from "../../../components/Utils/SkeletonGroup";

import { CartTransformAccordion } from "./CartTransformAccordion";
import { MetafieldLookupSection } from "./MetafieldLookupSection";

interface PricingStatusProps {
  organization: OrganizationDto;
}

interface CartTransformsSectionProps {
  cartTransformFeatures: CartTransformFeaturesType;
}

const ColorText = ({ value }: { value: boolean }) => {
  return (
    <IgTypography
      sx={{
        color: value ? ColorSuccess.main : ColorError.main,
        fontWeight: 600,
      }}
    >
      {value ? "Allowed" : "Not Allowed"}
    </IgTypography>
  );
};

const CartTransformFeaturesSection = ({
  cartTransformFeatures,
}: CartTransformsSectionProps) => {
  return (
    <Grid container item direction={"column"} sx={{ marginBottom: "10px" }}>
      <Grid item>
        <IgTypography>
          Update Operation:{" "}
          <ColorText value={cartTransformFeatures.updateOperation} /> by Shopify
        </IgTypography>
      </Grid>
      <Grid item>
        <IgTypography>
          Expand Operation:{" "}
          <ColorText value={cartTransformFeatures.expandOperation} /> by Shopify
        </IgTypography>
      </Grid>
      <Grid item>
        <IgTypography>
          Merge Operation:{" "}
          <ColorText value={cartTransformFeatures.mergeOperation} /> by Shopify
        </IgTypography>
      </Grid>
    </Grid>
  );
};

const ActiveModeSection = () => {
  const items = ["update", "expand", "merge"];
  const activeMode = useConfiguration<string>("cart_transform_mode")?.value;
  const [item, setItem] = useImmer<string>(activeMode || "update");

  const mutateConfigurations = usePatchConfigurations();
  const organizationId = useGetOrgId();

  const onClick = () => {
    const data: ConfigurationDto = {
      value: item,
      type: "String",
      key: "cart_transform_mode",
      topic: "PLUGIN_CONFIG",
      organizationId,
    };

    mutateConfigurations.mutate([data]);
  };

  return (
    <Grid container item direction={"column"} rowSpacing={1}>
      <Grid item>
        <IgTypography variant={"h6"}>Active Mode:</IgTypography>
      </Grid>
      <Grid container item columnSpacing={2}>
        <Grid item>
          <IgSelect
            value={item}
            onChange={(e: SelectChangeEvent<any>) => setItem(e.target.value)}
            sx={{ minWidth: "150px" }}
          >
            {items.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </IgSelect>
        </Grid>
        <Grid item>
          <IgButton
            variant={"contained"}
            onClick={onClick}
            loading={mutateConfigurations.isPending}
          >
            Save
          </IgButton>
        </Grid>
      </Grid>
      <Grid item>
        <IgTypography>
          * In general, use update for Shopify Plus stores and expand for
          Non-Plus stores.
        </IgTypography>
      </Grid>
    </Grid>
  );
};

const CartTransformsSection = () => {
  const cartTransforms = useGetCartTransforms();

  return (
    <Grid container item direction={"column"}>
      <Grid item sx={{ marginBottom: "10px" }}>
        <IgTypography variant="h6">Store Cart Transforms</IgTypography>
      </Grid>
      {cartTransforms.isSuccess ? (
        <Grid container item direction={"column"}>
          <CartTransformFeaturesSection
            cartTransformFeatures={cartTransforms.data.cartTransformFeatures}
          />
          {cartTransforms.data.cartTransforms.length > 1 ? (
            <IgTypography variant={"body2"}>
              Intelligems only uses 1 cart transform function. Multiple Cart
              Transforms may exist from other apps.
            </IgTypography>
          ) : null}
          {cartTransforms.data.cartTransforms.length > 0 ? (
            cartTransforms.data.cartTransforms.map((cartTransform, index) => (
              <CartTransformAccordion
                key={`${cartTransform.id}-${index}`}
                cartTransform={cartTransform}
                index={index}
                totalCount={cartTransforms.data.cartTransforms.length}
              />
            ))
          ) : (
            <IgTypography color={ColorError.main} sx={{ fontWeight: 600 }}>
              No Cart Transforms found
            </IgTypography>
          )}
        </Grid>
      ) : (
        <SkeletonGroup lines={3} />
      )}
    </Grid>
  );
};

export const PricingStatusSettingsSection = ({
  organization,
}: PricingStatusProps) => {
  const syncPricingFunction = useSyncPricingFunction();
  const pricingFunctionsEnabled = useConfiguration<boolean>(
    "shopify_functions_for_pricing_enabled"
  );

  return (
    <Grid container direction={"column"} rowSpacing={1}>
      <Grid item>
        <IgTypography variant="h6">
          Manage and Troubleshoot Pricing Functions
        </IgTypography>
      </Grid>
      {pricingFunctionsEnabled !== undefined ? (
        <IgTypography>
          Pricing Functions Configuration{" "}
          <IgTypography
            sx={{
              fontWeight: 600,
              color: pricingFunctionsEnabled.value
                ? ColorSuccess.main
                : ColorError.main,
            }}
          >
            {pricingFunctionsEnabled?.value ? "Enabled" : "Disabled"}
          </IgTypography>
        </IgTypography>
      ) : null}
      <Grid item>
        <IgButton
          variant={"contained"}
          loading={syncPricingFunction.isPending}
          onClick={() => {
            syncPricingFunction.mutate({});
            setTimeout(
              () => window.scrollTo({ top: 0, behavior: "smooth" }),
              1000
            );
          }}
        >
          Sync Pricing Function
        </IgButton>
      </Grid>
      <Grid item xs sx={{ marginTop: "10px" }}>
        <Divider />
      </Grid>
      <Grid item xs sx={{ marginTop: "10px" }}>
        <ActiveModeSection />
      </Grid>
      <Grid item xs>
        <CartTransformsSection />
      </Grid>
      <Grid item xs sx={{ marginTop: "10px" }}>
        <Divider />
      </Grid>
      <Grid item xs>
        <MetafieldLookupSection
          type={"variant"}
          metafieldKey={"variationPrices"}
        />
      </Grid>
    </Grid>
  );
};
